import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "stores/RootStore";
import ServiceStore from "stores/ServiceStore";

interface MixDesignApprovalRequest {
  id?: number;
  mixDesignId: number | null;
  requestedBy: number | null;
  requestDate: Date;
  requiredByDate: Date;

  // Mix Design Details
  mixName: string;
  strengthRequirement: string;
  slumpRequirement: number;
  airContentRequirement: number;

  // Purpose
  jobId: number | null;
  customerId: number | null;
  applicationPurpose: string;
  exposureConditions: string[];
  specialRequirements: string;

  // Materials
  cementType: string;
  aggregateTypes: string[];
  additives: string[];
  estimatedQuantity: number; // yards

  // Review
  reviewedBy: number | null;
  reviewDate: Date | null;
  approvalStatus: "pending" | "approved" | "rejected" | "revision_requested";
  reviewerComments: string;
  revisionsNeeded: string;

  // Lab Testing
  labTestsRequired: boolean;
  testResults: {
    testType: string;
    result: string;
    passedSpec: boolean;
  }[];

  // Documentation
  attachments: string[];
  notes: string;
}

class MixDesignApprovalWorkflowStore {
  rootStore: RootStore;

  loading = false;
  error: string | null = null;
  success: string | null = null;
  activeStep = 0;
  requestData: MixDesignApprovalRequest;
  pendingApprovals: any[] = [];

  constructor(rootStore: RootStore) {
    this.requestData = this.getDefaultRequest();
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  getDefaultRequest = (): MixDesignApprovalRequest => ({
    mixDesignId: null,
    requestedBy: null,
    requestDate: new Date(),
    requiredByDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),

    mixName: "",
    strengthRequirement: "4000 PSI",
    slumpRequirement: 4,
    airContentRequirement: 6,

    jobId: null,
    customerId: null,
    applicationPurpose: "",
    exposureConditions: [],
    specialRequirements: "",

    cementType: "Type I/II",
    aggregateTypes: [],
    additives: [],
    estimatedQuantity: 0,

    reviewedBy: null,
    reviewDate: null,
    approvalStatus: "pending",
    reviewerComments: "",
    revisionsNeeded: "",

    labTestsRequired: false,
    testResults: [],

    attachments: [],
    notes: "",
  });

  setLoading = (loading: boolean) => {
    this.loading = loading;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    this.activeStep--;
  };

  updateRequestData = <K extends keyof MixDesignApprovalRequest>(
    field: K,
    value: MixDesignApprovalRequest[K]
  ) => {
    this.requestData[field] = value;
  };

  validateCurrentStep = (): boolean => {
    this.setError(null);

    switch (this.activeStep) {
      case 0:
        if (!this.requestData.mixName) {
          this.setError("Mix design name is required");
          return false;
        }
        if (!this.requestData.requestedBy) {
          this.setError("Please specify who is requesting this approval");
          return false;
        }
        break;

      case 1:
        if (!this.requestData.jobId && !this.requestData.customerId) {
          this.setError("Please select a job or customer");
          return false;
        }
        if (!this.requestData.applicationPurpose) {
          this.setError("Please specify the application purpose");
          return false;
        }
        break;
    }

    return true;
  };

  loadInitialData = async () => {
    this.setLoading(true);
    try {
      const jobStore = this.rootStore.jobStore as ServiceStore;
      const customersStore = this.rootStore.customersStore as ServiceStore;
      const mixDesignsStore = this.rootStore.mixDesignsStore as ServiceStore;

      await Promise.all([
        jobStore?.find({ query: { archived: false } }),
        customersStore?.find(),
        mixDesignsStore?.find(),
      ]);
    } catch (err: any) {
      runInAction(() => this.setError("Failed to load data"));
    } finally {
      runInAction(() => this.setLoading(false));
    }
  };

  submitRequest = async () => {
    if (!this.validateCurrentStep()) return;

    this.setLoading(true);
    try {
      // TODO: Submit approval request when service is available
      runInAction(() => {
        this.setSuccess("Mix design approval request submitted!");
        this.activeStep = 3;
      });
    } catch (err: any) {
      runInAction(() => this.setError(err.message || "Failed to submit request"));
    } finally {
      runInAction(() => this.setLoading(false));
    }
  };

  reset = () => {
    this.loading = false;
    this.error = null;
    this.success = null;
    this.activeStep = 0;
    this.requestData = this.getDefaultRequest();
  };

  get jobs() {
    const jobStore = this.rootStore.jobStore as ServiceStore;
    return jobStore?.entities || [];
  }

  get customers() {
    const customersStore = this.rootStore.customersStore as ServiceStore;
    return customersStore?.entities || [];
  }

  get mixDesigns() {
    const mixDesignsStore = this.rootStore.mixDesignsStore as ServiceStore;
    return mixDesignsStore?.entities || [];
  }
}

export default MixDesignApprovalWorkflowStore;
